import { QueryClient, QueryFunction } from "@tanstack/react-query";

/**
 * Checks if the response status is ok, otherwise throws an error
 * with enhanced details about the error
 */
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // First, try to get a detailed error JSON from the server
    try {
      const errorData = await res.json();
      // Create an enhanced error object that preserves the error response details
      const enhancedError = new Error(`${res.status}: ${errorData.message || res.statusText}`);
      (enhancedError as any).response = res;
      (enhancedError as any).status = res.status;
      (enhancedError as any).details = errorData;
      throw enhancedError;
    } catch (jsonError: any) {
      // Re-throw if this is our own enhanced error (not a JSON parse failure)
      if (jsonError?.status) throw jsonError;
      // If we can't get JSON, fall back to text
      try {
        const text = await res.text();
        const enhancedError = new Error(`${res.status}: ${text || res.statusText}`);
        (enhancedError as any).response = res;
        (enhancedError as any).status = res.status;
        throw enhancedError;
      } catch (textError) {
        // If we can't get text either, use status text
        const enhancedError = new Error(`${res.status}: ${res.statusText}`);
        (enhancedError as any).response = res;
        (enhancedError as any).status = res.status;
        throw enhancedError;
      }
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: RequestInit
): Promise<Response> {
  const isFormData = data instanceof FormData;
  
  // Get JWT token from localStorage
  const token = localStorage.getItem('auth_token');
  
  const headers: Record<string, string> = {
    ...(data && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(token ? { "Authorization": `Bearer ${token}` } : {})
  };
  
  const res = await fetch(url, {
    method,
    headers,
    body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
    credentials: "include",
    ...options
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // The first item in queryKey is always the URL path
    let url = queryKey[0] as string;
    
    // If there are additional query parameters in the queryKey array, add them to the URL
    if (queryKey.length > 1) {
      const params = new URLSearchParams();
      
      // Check for common date parameters
      if (queryKey[1] && typeof queryKey[1] === 'string') {
        params.append('startDate', queryKey[1] as string);
      }
      
      if (queryKey[2] && typeof queryKey[2] === 'string') {
        params.append('endDate', queryKey[2] as string);
      }
      
      // If we have parameters, append them to the URL
      if (params.toString()) {
        url = `${url}?${params.toString()}`;
      }
    }
    
    // Get JWT token from localStorage
    const token = localStorage.getItem('auth_token');
    
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        ...(token ? { "Authorization": `Bearer ${token}` } : {})
      }
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,       // 30s — data is fresh for 30 seconds before refetching
      gcTime: 5 * 60 * 1000,      // 5min — keep unused data in cache to avoid re-fetching on remount
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Clears all business-specific query cache
 * Call this when switching businesses to prevent data leaking between businesses
 * @param businessId The ID of the business that was previously active
 */
export function clearBusinessCache(previousBusinessId: number | null) {
  if (previousBusinessId) {
    queryClient.removeQueries({
      predicate: (query) => {
        // Check every element in the query key for the previous business ID
        return query.queryKey.some((k) => {
          if (typeof k === 'string') return k.includes(`/api/businesses/${previousBusinessId}`) || k.includes(`/${previousBusinessId}`);
          if (typeof k === 'number') return k === previousBusinessId;
          return false;
        });
      },
    });
  }
}
